import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { etatDesLieuxApi } from '../services/api';
import signatureService from '../services/signature';
import SignaturePad from '../components/SignaturePad';

const SignEtatDesLieux = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const [landlordSignature, setLandlordSignature] = useState(null);
  const [tenantSignature, setTenantSignature] = useState(null);
  const [includeTenantSignature, setIncludeTenantSignature] = useState(false);
  const [useSavedForLandlord, setUseSavedForLandlord] = useState(true);

  useEffect(() => {
    fetchEtatDesLieux();
    fetchSavedSignature();
  }, [id]);

  useEffect(() => {
    if (useSavedForLandlord && savedSignature) {
      setLandlordSignature(savedSignature);
    } else if (!useSavedForLandlord) {
      setLandlordSignature(null);
    }
  }, [useSavedForLandlord, savedSignature]);

  const fetchEtatDesLieux = async () => {
    try {
      const response = await etatDesLieuxApi.getById(id);
      setEtat(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'état des lieux');
      navigate('/etat-des-lieux');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedSignature = async () => {
    try {
      const response = await signatureService.getUserSignature();
      if (response.exists) {
        setSavedSignature(response.signature.imageData);
        setLandlordSignature(response.signature.imageData);
      } else {
        setUseSavedForLandlord(false);
      }
    } catch (error) {
      console.error('Failed to fetch signature:', error);
      setUseSavedForLandlord(false);
    }
  };

  const handleSign = async () => {
    // Validate signatures
    if (!landlordSignature && !etat.landlordSigned) {
      toast.error('La signature du bailleur est requise');
      return;
    }

    if (includeTenantSignature && !tenantSignature && !etat.tenantSigned) {
      toast.error('Veuillez fournir la signature du locataire ou décocher l\'option');
      return;
    }

    try {
      setSubmitting(true);

      const signatures = {
        landlordSignature: etat.landlordSigned ? '' : landlordSignature,
        tenantSignature: includeTenantSignature && !etat.tenantSigned ? tenantSignature : '',
      };

      await etatDesLieuxApi.sign(id, signatures);
      toast.success('État des lieux signé avec succès');
      navigate(`/etat-des-lieux/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!etat) {
    return null;
  }

  if (etat.status === 'signed') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">État des lieux déjà signé</h2>
          <p className="text-gray-600 mb-6">
            Cet état des lieux a déjà été signé par les deux parties.
          </p>
          <Link
            to={`/etat-des-lieux/${id}`}
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Voir l'état des lieux
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Signer l'état des lieux
          </h1>
          <p className="text-gray-600 mt-2">
            {etat.type === 'entrée' ? 'Entrée' : 'Sortie'} - {etat.property?.name}
          </p>
        </div>

        {/* Signing Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">État des signatures</h3>
          <div className="space-y-1 text-sm">
            <p className={etat.landlordSigned ? 'text-green-700' : 'text-blue-800'}>
              {etat.landlordSigned ? '✓ Bailleur : Signé' : '○ Bailleur : Non signé'}
            </p>
            <p className={etat.tenantSigned ? 'text-green-700' : 'text-blue-800'}>
              {etat.tenantSigned ? '✓ Locataire : Signé' : '○ Locataire : Non signé'}
            </p>
          </div>
        </div>

        {/* Landlord Signature */}
        {!etat.landlordSigned && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Signature du bailleur</h2>

            {savedSignature && (
              <div className="mb-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={useSavedForLandlord}
                    onChange={(e) => setUseSavedForLandlord(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Utiliser ma signature enregistrée</span>
                </label>
              </div>
            )}

            {useSavedForLandlord && savedSignature ? (
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <p className="text-sm text-gray-600 mb-2">Signature enregistrée :</p>
                <img
                  src={savedSignature}
                  alt="Signature enregistrée"
                  className="border border-gray-300 rounded bg-white"
                  style={{ maxWidth: '300px', maxHeight: '150px' }}
                />
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Signez dans le cadre ci-dessous :
                </p>
                <SignaturePad
                  onSignatureChange={(signature) => setLandlordSignature(signature)}
                />
              </div>
            )}
          </div>
        )}

        {/* Tenant Signature Option */}
        {!etat.tenantSigned && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Signature du locataire</h2>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeTenantSignature}
                  onChange={(e) => setIncludeTenantSignature(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Inclure la signature du locataire maintenant</span>
              </label>
            </div>

            {includeTenantSignature && (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Le locataire peut signer dans le cadre ci-dessous :
                </p>
                <SignaturePad
                  onSignatureChange={(signature) => setTenantSignature(signature)}
                />
              </div>
            )}

            {!includeTenantSignature && (
              <p className="text-sm text-gray-500 italic">
                Le locataire pourra signer ultérieurement
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <Link
            to={`/etat-des-lieux/${id}`}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </Link>

          <button
            onClick={handleSign}
            disabled={submitting || (!landlordSignature && !etat.landlordSigned)}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Signature en cours...' : 'Signer'}
          </button>
        </div>

        {/* Info */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ℹ️ Après la signature, un PDF sera automatiquement généré avec les signatures des deux parties.
            {!includeTenantSignature && !etat.tenantSigned && (
              <span> Si le locataire n'a pas encore signé, il pourra le faire plus tard.</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignEtatDesLieux;
